import type { DashboardParameter } from "@t3tools/contracts";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { artifactOptionDatasetIds } from "./artifactLayout";
import {
  artifactDatasetRequestParameters,
  type ArtifactOptionsState,
  type ArtifactParameterValues,
} from "./artifactParameters";
import {
  artifactResolveRequest,
  artifactWidgetState,
  type ArtifactResolveData,
  type ArtifactWidgetState,
} from "./artifactResolve";
import {
  dashboardArtifactOptionsQueryOptions,
  dashboardArtifactResolveQueryOptions,
  type ArtifactOptionsFetcher,
  type ArtifactResolveTransport,
} from "./artifactsReactQuery";
import type { ArtifactSpec } from "./artifactTypes";

/** A clock for "Updated 6 min ago": `pinned` in stories and tests, else ticking. */
export function useArtifactNow(pinned?: Date, intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => pinned ?? new Date());
  useEffect(() => {
    if (pinned) return;
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [pinned, intervalMs]);
  return pinned ?? now;
}

export type ArtifactResolveResult = {
  /** Every data widget's state, from one stream. */
  widgetState: (widgetId: string) => ArtifactWidgetState;
  /** The stream is still open. */
  isStreaming: boolean;
  /** The current request succeeded with its required done line and is no longer fetching. */
  isComplete: boolean;
};

/**
 * Loads a whole dashboard through one resolve stream. While a new stream
 * catches up after a filter change, widgets keep the last finished stream's
 * numbers, dimmed, instead of flashing back to skeletons.
 */
export function useArtifactResolve(input: {
  artifactId: string;
  now: Date;
  parameters: ReadonlyArray<DashboardParameter>;
  /** Holds the stream (widgets keep the last numbers) while filters settle. */
  paused?: boolean | undefined;
  transport?: ArtifactResolveTransport | undefined;
  values: ArtifactParameterValues;
  versionId: string;
}): ArtifactResolveResult {
  const { artifactId, now, parameters, values, versionId } = input;
  // Date presets resolve to dates and query keys hash by content, so a ticking
  // clock only restarts the stream when the date in ET changes.
  const request = artifactResolveRequest(parameters, versionId, values, now);
  const query = useQuery({
    ...dashboardArtifactResolveQueryOptions({
      ref: artifactId,
      request,
      transport: input.transport,
    }),
    enabled: input.paused !== true,
  });
  const finished = query.data?.done ? query.data : undefined;
  const [previous, setPrevious] = useState<{
    data: ArtifactResolveData;
    versionId: string;
  } | null>(null);
  useEffect(() => {
    if (finished) setPrevious({ data: finished, versionId });
  }, [finished, versionId]);
  const fallback = previous?.versionId === versionId ? previous.data : undefined;

  const { data, error } = query;
  const widgetState = useCallback(
    (widgetId: string) =>
      artifactWidgetState({ current: data, error, previous: fallback, widgetId }),
    [data, error, fallback],
  );
  return {
    widgetState,
    isStreaming: query.isFetching,
    isComplete: query.isSuccess && query.fetchStatus === "idle" && data?.done === true,
  };
}

/**
 * Rows of each dataset that feeds a parameter's options, keyed by dataset id.
 * Options datasets bind date ranges and other selects with static options, so
 * they reload when those filters change; meanwhile the previous rows stay.
 */
export function useArtifactParameterOptions(input: {
  artifactId: string;
  fetchOptions?: ArtifactOptionsFetcher | undefined;
  now: Date;
  spec: Pick<ArtifactSpec, "datasets" | "parameters">;
  values: ArtifactParameterValues;
  versionId: string;
}): ArtifactOptionsState {
  const { spec } = input;
  const datasetIds = useMemo(() => artifactOptionDatasetIds(spec.parameters), [spec.parameters]);
  const results = useQueries({
    queries: datasetIds.map((datasetId) =>
      dashboardArtifactOptionsQueryOptions({
        ref: input.artifactId,
        request: {
          datasetId,
          versionId: input.versionId,
          parameters: artifactDatasetRequestParameters(
            spec.parameters,
            spec.datasets.find((dataset) => dataset.id === datasetId) ?? { bind: [] },
            input.values,
            input.now,
          ),
        },
        fetchOptions: input.fetchOptions,
      }),
    ),
  });
  return {
    data: new Map(datasetIds.map((datasetId, index) => [datasetId, results[index]?.data])),
    loading: new Set(
      datasetIds.filter(
        (_datasetId, index) => results[index]?.isPending || results[index]?.isPlaceholderData,
      ),
    ),
  };
}
